/**
 * Small presentational component set on top of the tokens in styles.css.
 * Purely visual: no data fetching, no behaviour. Class names used by tests are kept.
 */
import { useState } from 'react';
import type {
  CSSProperties,
  ReactNode,
  ButtonHTMLAttributes,
  HTMLAttributes,
  ThHTMLAttributes,
} from 'react';
import type { LucideIcon } from 'lucide-react';
import { Inbox } from 'lucide-react';
import { t } from '../i18n/index.js';

// ---------- Card ----------
export function Card({
  className = '',
  title,
  subtitle,
  actions,
  children,
  ...rest
}: /**
 * `title` is omitted from the HTML attributes before being re-declared: an HTMLElement's own
 * `title` is the tooltip string, and intersecting it with ReactNode gives `string & ReactNode`,
 * which quietly refuses anything but a string — so a card heading could never carry an icon.
 */
Omit<HTMLAttributes<HTMLElement>, 'title'> & {
  title?: ReactNode;
  subtitle?: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <section className={`card ${className}`} {...rest}>
      {(title || actions) && (
        <div className="card-head">
          <div>
            {title && <h3 className="card-title">{title}</h3>}
            {subtitle && <p className="card-sub">{subtitle}</p>}
          </div>
          {actions && <div className="card-actions">{actions}</div>}
        </div>
      )}
      {children}
    </section>
  );
}

// ---------- StatTile ----------
export type Tone = 'teal' | 'violet' | 'blue' | 'green' | 'amber' | 'red' | 'neutral';

export function StatTile({
  icon: Icon,
  label,
  value,
  tone = 'teal',
  hint,
  className = '',
}: {
  icon: LucideIcon;
  label: ReactNode;
  value: ReactNode;
  tone?: Tone;
  hint?: ReactNode;
  className?: string;
}) {
  return (
    <div className={`stat-tile tone-${tone} ${className}`}>
      <span className="stat-icon" aria-hidden="true">
        <Icon size={20} strokeWidth={2} />
      </span>
      <div className="stat-body">
        <span className="stat-label">{label}</span> <span className="stat-value">{value}</span>
        {hint && <span className="stat-hint">{hint}</span>}
      </div>
    </div>
  );
}

// ---------- Badge ----------
export function Badge({
  tone = 'neutral',
  className = '',
  children,
  ...rest
}: HTMLAttributes<HTMLSpanElement> & { tone?: Tone | 'warn' | 'bad' | 'good' }) {
  return (
    <span className={`badge ${tone} ${className}`} {...rest}>
      {children}
    </span>
  );
}

// ---------- Button ----------
export function Button({
  variant = 'primary',
  size = 'md',
  icon: Icon,
  className = '',
  children,
  ...rest
}: ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: 'primary' | 'secondary' | 'ghost' | 'danger';
  size?: 'sm' | 'md';
  icon?: LucideIcon;
}) {
  const cls = [variant === 'primary' ? '' : variant, size === 'sm' ? 'small' : '', className]
    .filter(Boolean)
    .join(' ');
  return (
    <button type="button" className={cls} {...rest}>
      {Icon && <Icon size={16} aria-hidden="true" />}
      {children}
    </button>
  );
}

// ---------- Tabs ----------
export function Tabs<T extends string>({
  value,
  onChange,
  items,
}: {
  value: T;
  onChange: (v: T) => void;
  items: { value: T; label: ReactNode }[];
}) {
  return (
    <div className="tabs" role="tablist">
      {items.map((it) => (
        <button
          key={it.value}
          type="button"
          role="tab"
          aria-selected={it.value === value}
          className={`tab ${it.value === value ? 'active' : ''}`}
          onClick={() => onChange(it.value)}
        >
          {it.label}
        </button>
      ))}
    </div>
  );
}

// ---------- Table ----------
export function Table({ children, className = '' }: { children: ReactNode; className?: string }) {
  return (
    // The class goes on the table as well as the wrapper: the phone layout is written as
    // `table.stacks`, and a class only on the wrapper never reached it — the salary slips table
    // was still a nine-column table clipped at the edge of a phone.
    <div className={`table-wrap ${className}`}>
      <table className={className}>{children}</table>
    </div>
  );
}
export const Th = (p: ThHTMLAttributes<HTMLTableCellElement>) => <th {...p} />;

// ---------- Skeleton ----------
export function Skeleton({ lines = 3, className = '' }: { lines?: number; className?: string }) {
  return (
    <div className={`skeleton ${className}`} aria-busy="true" aria-live="polite">
      {Array.from({ length: lines }, (_, i) => (
        <span key={i} className="skeleton-line" style={{ width: `${92 - (i % 3) * 18}%` }} />
      ))}
    </div>
  );
}

// ---------- Avatar ----------
export function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  const a = parts[0]?.[0] ?? '?';
  const b = parts.length > 1 ? parts[parts.length - 1]![0] : '';
  return (a + (b ?? '')).toUpperCase();
}

/** Stable hue per name so the same worker always gets the same colour. */
function hueOf(name: string): number {
  let h = 0;
  for (const ch of name) h = (h * 31 + ch.charCodeAt(0)) % 360;
  return h;
}

/*
 * The disc and its initials are coloured in the stylesheet, from the hue alone (see .avatar).
 *
 * They used to be chosen here, a saturated disc with white or near-black initials, measured to a
 * worst case of 5.99:1. The October 2026 redesign wanted soft discs, and the pair that gives them
 * — a pastel disc with dark initials of the same hue on the light theme, a deep disc with pale
 * initials on the dark one — measures better: 6.54:1 and 7.01:1 at the worst of all 360 hues
 * (yellow, hue 60, both times). It has to live in CSS because only the stylesheet knows which
 * theme is showing.
 */

/**
 * Somebody's face, or their initials when there is no photograph.
 *
 * Initials stay as the fallback rather than a grey silhouette: on a roster where several people
 * share a first name, two identical outlines side by side are worse than nothing.
 *
 * It stays `aria-hidden` either way, photograph or not. An avatar is always printed beside the
 * name it belongs to, so anything here is said twice to a screen reader.
 *
 * `photoUrl` is expected to carry its own cache-buster, because a new photograph at an unchanged
 * URL is a face that stays wrong until somebody clears their browser.
 */
export function Avatar({
  name,
  size = 32,
  photoUrl,
  onOpen,
}: {
  name: string;
  size?: number;
  photoUrl?: string | null;
  /**
   * Called when the photograph is pressed, for showing it full size.
   *
   * Only ever wired where there is something to open. Without it the avatar stays what it was —
   * decoration beside a name — and nothing on the page pretends to be pressable.
   */
  onOpen?: () => void;
}) {
  const hue = hueOf(name);
  /**
   * A photograph that will not load is the initials again, not a broken-image icon.
   *
   * This is the offline case as much as the missing-file one: the last known `/auth/me` is kept
   * for a worker with no signal, so a phone at the gate can know a photograph exists and still
   * have no way to fetch it. Keyed on the URL so a new photograph gets a fresh try.
   */
  const [failed, setFailed] = useState<string | null>(null);
  const src = photoUrl && failed !== photoUrl ? photoUrl : null;
  const inner = src ? (
    <img src={src} alt="" loading="lazy" onError={() => setFailed(src)} />
  ) : (
    initialsOf(name)
  );
  // The hue is the person's; the stylesheet turns it into a disc for the theme on screen.
  const style = {
    width: size,
    height: size,
    fontSize: size * 0.38,
    '--av-h': hue,
    ...(src ? { background: 'transparent' } : {}),
  } as CSSProperties;
  /*
   * With a photograph and somewhere to open it, the avatar is a real button — focusable, named,
   * and announced — rather than a span with a click handler. Without one it stays aria-hidden
   * decoration: the name it belongs to is always printed beside it.
   */
  if (src && onOpen) {
    return (
      <button
        type="button"
        className="avatar avatar-open"
        style={style}
        onClick={onOpen}
        title={t('photo_view', { name })}
        aria-label={t('photo_view', { name })}
      >
        {inner}
      </button>
    );
  }
  return (
    <span className="avatar" style={style} aria-hidden="true">
      {inner}
    </span>
  );
}

// ---------- EmptyState ----------
export function EmptyState({
  icon: Icon = Inbox,
  text,
  className = '',
}: {
  icon?: LucideIcon;
  text: ReactNode;
  className?: string;
}) {
  return (
    <div className={`empty ${className}`}>
      <Icon size={22} aria-hidden="true" />
      <span>{text}</span>
    </div>
  );
}

// ---------- Meter (cap / progress) ----------
export function Meter({
  value,
  max,
  tone = 'teal',
  label,
}: {
  value: number;
  max: number;
  tone?: Tone;
  label?: ReactNode;
}) {
  const pct = max > 0 ? Math.min(100, Math.round((value / max) * 100)) : 0;
  const full = max > 0 && value >= max;
  const over = max > 0 && value > max;
  return (
    <div className={`meter tone-${over ? 'red' : full ? 'green' : tone}`} title={`${value}/${max}`}>
      {label && <span className="meter-label">{label}</span>}
      <span className="meter-track">
        <span className="meter-fill" style={{ width: `${pct}%` }} />
      </span>
    </div>
  );
}

// ---------- Bar chart (inline SVG, no library) ----------
export type Bar = { key: string; label: string; value: number; target?: number; tone?: Tone };

/**
 * Vertical bars with a y-axis, a value on every bar, and a labelled target line.
 *
 * The chart draws in its own pixel space and is scaled to fit, rather than stretched to the
 * container: a stretched viewBox distorts text, which is why this chart carried no numbers at
 * all before. Every bar also has a title, so hovering gives the exact figures.
 */
export function BarChart({
  bars,
  max,
  height = 200,
  format,
  targetLabel,
}: {
  bars: Bar[];
  max?: number;
  height?: number;
  format?: (v: number) => string;
  /** What the target line means, for its label. */
  targetLabel?: string;
}) {
  if (bars.length === 0) return null;
  const fmt = format ?? ((v: number) => String(v));
  const raw = Math.max(max ?? 0, ...bars.map((b) => Math.max(b.value, b.target ?? 0)), 1);
  // Round the axis up so the ticks land on readable numbers rather than on the tallest bar.
  const step = niceStep(raw / 4);
  const top = Math.max(step * 4, step * Math.ceil(raw / step));
  const ticks = Array.from({ length: 5 }, (_, i) => (top / 4) * i);

  // Pixel space: a fixed drawing area that is scaled as a whole, so the type keeps its shape.
  const targets = [...new Set(bars.map((b) => b.target).filter((t): t is number => !!t && t > 0))];
  // Wide enough for the longest tick label ("13h 20m") without clipping it.
  // The names are drawn inside the SVG, under the bars, so they scale with the bars. A grid of
  // HTML labels under a scaled SVG never lined up with them: two bars, two names at the edges.
  const padL = 60;
  const padR = 10;
  const padT = 18;
  const padB = 18;
  // Sixty-four per bar: enough for a ten-character name at the label size, which is what
  // "Basavaraj T" needs. Thirty of them scroll sideways at full size rather than shrink.
  const W = Math.max(320, bars.length * 64 + padL + padR);
  const plotW = W - padL - padR;
  const plotH = height - padT - padB;
  const y = (v: number) => padT + plotH - (v / top) * plotH;
  const slot = plotW / bars.length;
  const bw = Math.min(slot * 0.6, 34);

  return (
    // A roster of thirty does not fit on a screen at a size anyone can read. Rather than shrink
    // the numbers until they are decorative, the chart keeps its size and scrolls.
    <div className="chart scroller">
      <div className="chart-inner" style={{ minWidth: W }}>
        <svg viewBox={`0 0 ${W} ${height}`} role="img" aria-label="Bar chart">
        {ticks.map((v) => (
          <g key={v}>
            <line x1={padL} x2={W - padR} y1={y(v)} y2={y(v)} className="chart-grid" />
            <text x={padL - 6} y={y(v) + 3.5} className="chart-axis" textAnchor="end">
              {fmt(v)}
            </text>
          </g>
        ))}
        {bars.map((b, i) => {
          const x = padL + i * slot + (slot - bw) / 2;
          const barTop = y(b.value);
          const ty = b.target !== undefined && b.target > 0 ? y(b.target) : null;
          return (
            <g key={b.key}>
              <title>
                {`${b.label}: ${fmt(b.value)}${b.target ? ` of ${fmt(b.target)} ${targetLabel ?? 'target'}` : ''}`}
              </title>
              <rect
                x={x}
                y={barTop}
                width={bw}
                height={Math.max(0, padT + plotH - barTop)}
                rx="3"
                className={`chart-bar tone-${b.tone ?? 'teal'}`}
              />
              {/* Always above the bar: a value inside a 34px bar was wider than the bar, and
                  one at the top of a full bar sat on the target line. Above is readable in both
                  themes, in the page's own colour. */}
              <text
                x={x + bw / 2}
                y={Math.max(padT - 6, barTop - 5)}
                className="chart-value"
                textAnchor="middle"
              >
                {fmt(b.value)}
              </text>
              <text
                x={x + bw / 2}
                y={padT + plotH + 13}
                className="chart-name"
                textAnchor="middle"
              >
                {b.label.length > 10 ? `${b.label.slice(0, 9)}…` : b.label}
              </text>
              {ty !== null && (
                <line
                  x1={x - bw * 0.2}
                  x2={x + bw * 1.2}
                  y1={ty}
                  y2={ty}
                  className="chart-target"
                />
              )}
            </g>
          );
        })}
        {/* Each distinct target gets one labelled line across the chart, so the reader can see
            what the ticks above the bars are measured against. In practice there are two: the
            full-time day and the part-time day. */}
        {targets.map((tv) => (
          <g key={`t-${tv}`}>
            <line
              x1={padL}
              x2={W - padR}
              y1={y(tv)}
              y2={y(tv)}
              className="chart-target faint"
            />
          </g>
        ))}
      </svg>
      </div>
    </div>
  );
}

/** 1, 2, 2.5 or 5 times a power of ten: the steps people read without doing arithmetic. */
function niceStep(rough: number): number {
  if (rough <= 0) return 1;
  const pow = Math.pow(10, Math.floor(Math.log10(rough)));
  const n = rough / pow;
  return (n <= 1 ? 1 : n <= 2 ? 2 : n <= 2.5 ? 2.5 : n <= 5 ? 5 : 10) * pow;
}

/**
 * One stacked bar per day, with a y-axis and the day's total above each bar. Same pixel-space
 * approach as BarChart: scaled as a whole so the numbers stay legible.
 */
export function StackedMini({
  days,
  height = 170,
  valueOf,
}: {
  days: { label: string; parts: { value: number; tone: Tone; label?: string }[] }[];
  height?: number;
  /**
   * What the number above each bar is. By default the bar's total — which on the attendance
   * chart was the roster that day, present + absent + off, a figure the owner rightly asked the
   * point of. The caller can say which part matters; the bar's height still shows the total.
   */
  valueOf?: (parts: { value: number; tone: Tone; label?: string }[], total: number) => number;
}) {
  if (days.length === 0) return null;
  const totals = days.map((d) => d.parts.reduce((n, p) => n + p.value, 0));
  const raw = Math.max(1, ...totals);
  const step = niceStep(raw / 3);
  const top = Math.max(step * 3, step * Math.ceil(raw / step));
  const ticks = Array.from({ length: 4 }, (_, i) => (top / 3) * i);

  const padL = 30;
  const padR = 8;
  const padT = 16;
  const padB = 4;
  const W = Math.max(260, days.length * 46 + padL + padR);
  const plotW = W - padL - padR;
  const plotH = height - padT - padB;
  const yOf = (v: number) => padT + plotH - (v / top) * plotH;
  const slot = plotW / days.length;
  const bw = Math.min(slot * 0.56, 26);

  return (
    <div className="chart mini">
      <svg viewBox={`0 0 ${W} ${height}`} role="img" aria-label="Days this week">
        {ticks.map((v) => (
          <g key={v}>
            <line x1={padL} x2={W - padR} y1={yOf(v)} y2={yOf(v)} className="chart-grid" />
            <text x={padL - 6} y={yOf(v) + 3.5} className="chart-axis" textAnchor="end">
              {Math.round(v)}
            </text>
          </g>
        ))}
        {days.map((d, i) => {
          const x = padL + i * slot + (slot - bw) / 2;
          const total = totals[i] ?? 0;
          const shown = valueOf ? valueOf(d.parts, total) : total;
          let cursor = padT + plotH;
          return (
            <g key={d.label}>
              <title>
                {`${d.label}: ${d.parts
                  .filter((p) => p.value > 0)
                  .map((p) => `${p.value} ${p.label ?? p.tone}`)
                  .join(', ') || 'nothing recorded'}`}
              </title>
              {d.parts.map((p, j) => {
                const h = (p.value / top) * plotH;
                cursor -= h;
                return (
                  <rect
                    key={j}
                    x={x}
                    y={cursor}
                    width={bw}
                    height={h}
                    className={`chart-bar tone-${p.tone}`}
                  />
                );
              })}
              {shown > 0 && (
                <text
                  x={x + bw / 2}
                  y={yOf(total) - 5}
                  className="chart-value"
                  textAnchor="middle"
                >
                  {shown}
                </text>
              )}
            </g>
          );
        })}
      </svg>
      <div className="chart-labels" style={{ gridTemplateColumns: `repeat(${days.length}, 1fr)` }}>
        {days.map((d) => (
          <span key={d.label}>{d.label}</span>
        ))}
      </div>
    </div>
  );
}
